// shop/cart.js — состояние корзины
// =============================================================================
// Хранится в localStorage: только slug, количество и параметры заказа.
// Название, цена и фото берутся из каталога, встроенного в страницу
// (<script type="application/json" id="sm-catalog">), поэтому корзина
// не может «запомнить» устаревшую цену.

const KEY = 'sm-cart-v1';

// Порог бесплатной доставки, ₽. null = акции нет, и покупатель видит ровно
// тот тариф, который посчитал СДЭК. Ставьте число, только если готовы платить
// за доставку из своей маржи — это ваше бизнес-решение, а не тариф перевозчика.
export const FREE_DELIVERY_FROM = null;

const listeners = new Set();

function defaults() {
  return {
    items: [],
    meta: { packaging: 'gift', delivery: 'pickup' },
    // Последний расчёт СДЭК. cityCode — ключ ко всему: без него нет ни
    // списка пунктов выдачи, ни тарифа.
    shipping: {
      city: '', cityCode: 0, pvz: null,
      cost: 0, minDays: 0, maxDays: 0, date: '',
      test: false, quoteError: 'no-city',
    },
  };
}

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return defaults();
    const parsed = JSON.parse(raw);
    const base = defaults();
    return {
      items: Array.isArray(parsed.items) ? parsed.items.filter(i => i && i.slug && i.qty > 0) : [],
      meta: Object.assign(base.meta, parsed.meta || {}),
      shipping: Object.assign(base.shipping, parsed.shipping || {}),
    };
  } catch (e) {
    return defaults();
  }
}

function write(state) {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch (e) {
    // Приватный режим / переполнение — корзина живёт до перезагрузки.
  }
  listeners.forEach(fn => fn(state));
}

// ── Каталог ─────────────────────────────────────────────────────────────────
let catalogCache = null;

export function catalog() {
  if (catalogCache) return catalogCache;
  const node = document.getElementById('sm-catalog');
  try {
    catalogCache = node ? JSON.parse(node.textContent) : [];
  } catch (e) {
    catalogCache = [];
  }
  return catalogCache;
}

export function findProduct(slug) {
  return catalog().find(p => p.slug === slug) || null;
}

export function findArticle(slug, articleId) {
  const product = findProduct(slug);
  if (!product || !Array.isArray(product.articles)) return null;
  return product.articles.find(a => String(a.id) === String(articleId)) || null;
}

// Позиция корзины — это конкретный экземпляр, а не модель: два кулона
// «Северное сияние» с разными номерами существуют по отдельности.
function sameLine(item, slug, articleId) {
  return item.slug === slug && String(item.articleId || '') === String(articleId || '');
}

// ── Чтение ──────────────────────────────────────────────────────────────────
export function state() {
  return read();
}

export function qtyOf(slug, articleId) {
  const found = read().items.find(i => sameLine(i, slug, articleId));
  return found ? found.qty : 0;
}

// Сколько экземпляров этой модели уже в корзине — для витрины, где номер
// экземпляра ещё не выбран.
export function qtyOfProduct(slug) {
  return read().items
    .filter(i => i.slug === slug)
    .reduce((sum, i) => sum + i.qty, 0);
}

export function count() {
  return read().items.reduce((sum, i) => sum + i.qty, 0);
}

// Строки корзины, обогащённые данными каталога. Позиции, которых больше нет
// в каталоге (или экземпляр которых исчез), отбрасываются — иначе корзина
// показывала бы вещь, которую невозможно заказать.
export function lines() {
  return read().items
    .map(i => {
      const product = findProduct(i.slug);
      if (!product) return null;

      const article = i.articleId ? findArticle(i.slug, i.articleId) : null;
      if (i.articleId && !article) return null;

      return {
        slug: i.slug,
        articleId: i.articleId || '',
        article: article,
        qty: i.qty,
        product: product,
        // Фото — конкретного экземпляра, а не модели.
        image: (article && article.image) || product.image,
        total: product.price * i.qty,
      };
    })
    .filter(Boolean);
}

// Общий вес заказа в граммах — нужен перевозчику для расчёта.
// К товару добавляем упаковку: коробка и прокладки тоже весят.
const PACKAGING_WEIGHT = 150;

export function weight() {
  return lines().reduce((sum, l) => sum + (l.product.weight || 100) * l.qty, 0) + PACKAGING_WEIGHT;
}

export function totals() {
  const state = read();
  const goods = lines().reduce((sum, l) => sum + l.total, 0);

  // Самовывоз бесплатен всегда. Порог бесплатной доставки — необязательная
  // акция магазина; пока FREE_DELIVERY_FROM = null, платит покупатель по
  // тарифу СДЭК.
  const isPickup = state.meta.delivery === 'pickup';
  const freeByPromo = FREE_DELIVERY_FROM !== null && goods >= FREE_DELIVERY_FROM;

  const raw = Number(state.shipping.cost) || 0;
  const delivery = isPickup || freeByPromo ? 0 : raw;

  return {
    goods: goods,
    delivery: delivery,
    freeDelivery: delivery === 0,
    // Показываем, что доставка бесплатна не «потому что 0», а по причине.
    freeReason: isPickup ? 'pickup' : (freeByPromo ? 'promo' : ''),
    test: Boolean(state.shipping.test) && delivery > 0,
    total: goods + delivery,
  };
}

// ── Запись ──────────────────────────────────────────────────────────────────
// Экземпляр физически один, поэтому количество по позиции всегда 1: повторное
// добавление того же номера ничего не меняет. Хочется два похожих — выбирайте
// второй экземпляр, у него свой номер и своя фотография.
export const MAX_PER_ARTICLE = 1;

export function add(slug, articleId) {
  const s = read();
  if (s.items.some(i => sameLine(i, slug, articleId))) return;
  s.items.push({ slug: slug, articleId: String(articleId || ''), qty: 1 });
  write(s);
}

export function setQty(slug, articleId, qty) {
  const s = read();
  const next = Math.min(MAX_PER_ARTICLE, Math.max(0, qty));
  const found = s.items.find(i => sameLine(i, slug, articleId));

  if (!found) {
    if (next > 0) s.items.push({ slug: slug, articleId: String(articleId || ''), qty: next });
  } else if (next === 0) {
    s.items = s.items.filter(i => !sameLine(i, slug, articleId));
  } else {
    found.qty = next;
  }
  write(s);
}

export function remove(slug, articleId) {
  const s = read();
  s.items = s.items.filter(i => !sameLine(i, slug, articleId));
  write(s);
}

export function clear() {
  write(defaults());
}

export function setMeta(key, value) {
  const s = read();
  s.meta[key] = value;
  write(s);
}

export function setShipping(patch) {
  const s = read();
  s.shipping = Object.assign(s.shipping, patch || {});
  write(s);
}

export function shipping() {
  return read().shipping;
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Корзина, открытая в двух вкладках, должна сходиться.
window.addEventListener('storage', e => {
  if (e.key === KEY) listeners.forEach(fn => fn(read()));
});

export function formatPrice(value) {
  return new Intl.NumberFormat('ru-RU').format(value) + ' ₽';
}
